import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  History as HistoryIcon,
  QrCode2 as BarcodeIcon,
  BluetoothRounded as BluetoothIcon,
  BluetoothConnectedRounded as BluetoothOnIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import PreviousLoansPanel from "./PreviousLoansPanel";
import {
  BG_ALT,
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_MED,
  BLUE_PALE,
  BORDER,
  BORDER_MED,
  INK,
  INK_MUTED,
  INK_SOFT,
  SUCCESS,
} from "./shared";

// ─── Right column: sticky ticket-preview + live deal summary, the Save/Print
// action buttons, and the customer's previous-loans rail. Extracted verbatim
// from PaymentScreen; all state/handlers/derived totals arrive as props.
const SummaryPanel = ({
  formData,
  loanAmt,
  intAmt,
  payAmt,
  schemes,
  deductOneMonth,
  saveButtonRef,
  // A Store Staff session viewing a payment already on file: no Save at all.
  saveHidden,
  loading,
  handleSave,
  voucherBlocked,
  voucherMissing,
  handlePrint,
  handlePrintBarcode,
  btSupported,
  btState,
  onOpenBtPrinter,
  storeId,
  pawnMode,
  currentFormattedVoucherNo,
  currentVoucherNo,
  loadPawnForEdit,
}) => {
  // Pairs with the lg={9} main column in PaymentScreen — below lg this panel is
  // a full-width block under the form rather than a 225px sliver beside it, so
  // the sticky offset comes off too.
  return (
              <Grid item xs={12} lg={3}>
                {/* ── Pawn Ticket Preview ─────────────────────────────────────── */}
                <Card
                  sx={{
                    mb: 2,
                    position: { xs: "static", lg: "sticky" },
                    top: { lg: 16 },
                  }}
                >
                  <Box
                    sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${BLUE}, ${BLUE_MED})`,
                    }}
                  />
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.25,
                      }}
                    >
                      <PaymentIcon sx={{ color: BLUE, fontSize: 18 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: INK }}
                      >
                        Pawn Ticket Preview
                      </Typography>
                    </Box>

                    {/* Compact 2-per-row layout: customer + loan, maturity +
                      rate, and the scheme row only when one is selected */}
                    <Grid container spacing={0.75}>
                      {[
                        {
                          label: "Customer",
                          value: formData.customerName || "—",
                          bold: true,
                        },
                        {
                          label: "Loan Amount",
                          value: loanAmt
                            ? `₹${loanAmt.toLocaleString("en-IN")}`
                            : "—",
                          bold: true,
                          color: BLUE,
                        },
                        {
                          label: "Maturity Date",
                          value: formData.collectByDate
                            ? dayjs(formData.collectByDate).format(
                                "DD MMM YYYY",
                              )
                            : "—",
                        },
                        {
                          label: "Interest Rate",
                          value: formData.interestRate
                            ? `${Number(formData.interestRate).toFixed(
                                2,
                              )}% / month`
                            : "—",
                        },
                        ...(formData.scheme &&
                        schemes.find((s) => s.id === formData.scheme)
                          ?.scheme_name
                          ? [
                              {
                                label: "Scheme",
                                value: schemes.find(
                                  (s) => s.id === formData.scheme,
                                ).scheme_name,
                              },
                            ]
                          : []),
                      ].map(({ label, value, bold, color }) => (
                        // Two per row in the narrow lg rail, but three across
                        // once the panel goes full-width below lg — otherwise a
                        // tablet shows two fields on a 900px-wide card.
                        <Grid item xs={6} sm={4} lg={6} key={label}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: INK_MUTED,
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={bold ? 700 : 500}
                            sx={{
                              color: color || INK,
                              fontSize: "0.8rem",
                              lineHeight: 1.3,
                              wordBreak: "break-word",
                            }}
                          >
                            {value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>

                    {/* ── Deal Summary (live) — verify the money before issuing ──── */}
                    <Box
                      sx={{
                        mt: 1.25,
                        p: 1.25,
                        borderRadius: "10px",
                        bgcolor: BLUE_PALE,
                        border: `1px solid ${BLUE_LIGHT}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 700,
                          color: BLUE_DARK,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mb: 1,
                        }}
                      >
                        Deal Summary
                      </Typography>
                      <Stack spacing={0.75}>
                        {[
                          {
                            label: "Item Value",
                            value: formData.itemValue
                              ? `₹${Number(formData.itemValue).toLocaleString(
                                  "en-IN",
                                )}`
                              : "—",
                          },
                          {
                            label: "Loan Amount",
                            value: loanAmt
                              ? `₹${loanAmt.toLocaleString("en-IN")}`
                              : "—",
                          },
                          // Interest AND insurance are only deducted from the
                          // payout when "Deduct 1 month interest" is checked —
                          // hide them otherwise so the summary matches the money.
                          ...(deductOneMonth
                            ? [
                                {
                                  label: "Interest",
                                  value: intAmt
                                    ? `₹${intAmt.toLocaleString("en-IN")}`
                                    : "—",
                                },
                              ]
                            : []),
                          ...(deductOneMonth && formData.insuranceAmount
                            ? [
                                {
                                  label: "Insurance",
                                  value: `₹${Number(
                                    formData.insuranceAmount,
                                  ).toLocaleString("en-IN")}`,
                                },
                              ]
                            : []),
                          ...(parseFloat(formData.otherCharges) > 0
                            ? [
                                {
                                  label: "Other Charges",
                                  value: `₹${Number(
                                    formData.otherCharges,
                                  ).toLocaleString("en-IN")}`,
                                },
                              ]
                            : []),
                        ].map(({ label, value }) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: INK_MUTED }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: INK_SOFT,
                                fontFamily: "monospace",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 0.5, borderColor: BLUE_LIGHT }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: INK }}
                          >
                            Pay to Customer
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: BLUE_DARK,
                              fontFamily: "monospace",
                            }}
                          >
                            {payAmt
                              ? `₹${payAmt.toLocaleString("en-IN")}`
                              : "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: BORDER }} />

                    <Stack spacing={1}>
                      {!saveHidden && (
                        <Button
                          ref={saveButtonRef}
                          fullWidth
                          variant="contained"
                          size="large"
                          startIcon={loading ? null : <SaveIcon />}
                          onClick={handleSave}
                          disabled={loading || voucherBlocked}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSave();
                            }
                          }}
                          sx={{
                            py: 1.4,
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                            "&:disabled": { opacity: 0.6 },
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={20} sx={{ color: "#fff" }} />
                          ) : (
                            "Save Payment"
                          )}
                        </Button>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="medium"
                          startIcon={<PrintIcon />}
                          onClick={() => handlePrint(formData)}
                          disabled={voucherBlocked || voucherMissing}
                          sx={{ fontWeight: 600 }}
                        >
                          Print Receipt
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="medium"
                          startIcon={<BarcodeIcon />}
                          onClick={handlePrintBarcode}
                          disabled={voucherBlocked || voucherMissing}
                          sx={{ fontWeight: 600 }}
                        >
                          Print Barcode
                        </Button>
                        {/* Only where a browser can actually reach a printer
                            over Bluetooth — a button that can never work is
                            worse than no button. Green once the printer is
                            live, so the operator can tell before saving
                            whether the label has somewhere to go. */}
                        {btSupported && (
                          <Tooltip
                            title={
                              btState?.connected
                                ? `Bluetooth printer: ${btState.deviceName || "connected"}`
                                : "Set up a Bluetooth label printer"
                            }
                          >
                            <IconButton
                              onClick={onOpenBtPrinter}
                              sx={{
                                flex: "0 0 auto",
                                border: `1.5px solid ${
                                  btState?.connected ? SUCCESS : BORDER_MED
                                }`,
                                borderRadius: "8px",
                                color: btState?.connected ? SUCCESS : INK_MUTED,
                              }}
                            >
                              {btState?.connected ? (
                                <BluetoothOnIcon fontSize="small" />
                              ) : (
                                <BluetoothIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                {/* ── Previous Loans ──────────────────────────────────────────── */}
                <Card>
                  <Box sx={{ height: 3, bgcolor: BG_ALT }} />
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <HistoryIcon sx={{ color: INK_MUTED, fontSize: 17 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: INK }}
                      >
                        Previous Loans
                      </Typography>
                      {formData.customerName && (
                        <Chip
                          label={formData.customerName.split(" ")[0]}
                          size="small"
                          sx={{
                            ml: "auto",
                            height: 18,
                            fontSize: "0.65rem",
                            bgcolor: BLUE_LIGHT,
                            color: BLUE_DARK,
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                    <PreviousLoansPanel
                      customerId={formData.customerId}
                      storeId={storeId}
                      pawnMode={pawnMode}
                      currentVoucher={
                        currentFormattedVoucherNo || currentVoucherNo
                      }
                      onSelectLoan={loadPawnForEdit}
                    />
                  </CardContent>
                </Card>
              </Grid>
  );
};

export default SummaryPanel;
